// HTTP client — mobile-portable per ADR-0009 principle 1.
// Supports Bearer token (mobile) AND cookies (current web) simultaneously.
// Retry policy: 1 automatic retry for GET requests on network errors only.
import axios from "axios";
import { config } from "./config";
import { createLogger } from "./logger";

const log = createLogger("http");

// Token storage key. Aligned with future expo-secure-store migration.
const TOKEN_KEY = "rf_auth_token";

export const tokenStore = {
  get: () => {
    try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
  },
  set: (t) => {
    try { localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ }
  },
  clear: () => {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  },
};

export const http = axios.create({
  baseURL: config.apiBaseUrl,
  timeout: 20000,
  withCredentials: true,
});

http.interceptors.request.use((cfg) => {
  const token = tokenStore.get();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  log.debug((cfg.method || "GET").toUpperCase(), cfg.url);
  return cfg;
});

http.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config || {};
    const method = (cfg.method || "get").toLowerCase();
    const isNetworkErr = !err.response;
    const shouldRetry = method === "get" && !cfg._retried && isNetworkErr;
    if (shouldRetry) {
      cfg._retried = true;
      await new Promise((r) => setTimeout(r, 500));
      return http.request(cfg);
    }
    if (err.response) {
      log.warn(cfg.url, err.response.status, err.response.data?.detail || "");
    } else {
      log.error(cfg.url, "network error", err.message);
    }
    return Promise.reject(err);
  }
);

export const formatApiError = (err, fallback = "Ocurrió un error") => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  return String(detail);
};

// Typed helpers — return unwrapped data.
export const apiGet    = (url, params) => http.get(url, { params }).then((r) => r.data);
export const apiPost   = (url, data, opts) => http.post(url, data, opts).then((r) => r.data);
export const apiPut    = (url, data) => http.put(url, data).then((r) => r.data);
export const apiDelete = (url) => http.delete(url).then((r) => r.data);
